import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../../config";
import Notification from "../../components/Notification";
import ConfirmDialog from "../../components/ConfirmDialog";
import { formatToDayMonthYear } from "../../utils/TextUtils";
import { allowedToView } from "../../utils/PermissionUtils";

function DocumentDetails() {
    const { id } = useParams();
    const [documentItem, setDocumentItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");

    const canEdit = (userRole) => allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator']);

    const fetchDocument = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/documents/${id}`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setDocumentItem(data);
            } else if (response.status === 404) {
                setError("Document not found.");
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                throw new Error("Failed to fetch document details");
            }
        } catch (error) {
            console.error("Fetch Document Error:", error);
            setError("An error occurred while fetching document details.");
        } finally {
            setLoading(false);
        }
    }, [id, token, navigate, location]);

    useEffect(() => {
        fetchDocument();
    }, [fetchDocument]);

    const handleDownload = async () => {
        try {
            const response = await fetch(`${API_URL}/documents/${id}/download`, {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                
                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = documentItem.name;
                
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                    if (filenameMatch && filenameMatch.length === 2) {
                        filename = filenameMatch[1];
                    }
                }
                
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                
                setNotification({ type: "success", message: 'Document download started' });
            } else {
                setNotification({ type: "danger", message: 'Failed to download document' });
            }
        } catch (error) {
            console.error("Download Error:", error);
            setNotification({ type: 'danger', message: 'An error occurred while downloading the document.' });
        }
    };

    const handleDeleteClick = () => {
        setIsConfirmDialogOpen(true);
    };

    const confirmDelete = async () => {
        try {
            const response = await fetch(`${API_URL}/documents/${id}`, {
                method: 'DELETE',
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (response.ok) {
                setNotification({ type: "success", message: 'Document deleted successfully' });
                setTimeout(() => {
                    navigate("/dashboard/documents");
                }, 1500);
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else if (response.status === 404) {
                setNotification({ type: "danger", message: 'Document not found.' });
            } else {
                const errorData = await response.json();
                setNotification({ type: "danger", message: errorData.message || 'There was an error deleting the document.' });
            }
        } catch (error) {
            setNotification({ type: 'danger', message: 'An error occurred while deleting the document.' });
            console.error("Delete Error:", error);
        } finally {
            setIsConfirmDialogOpen(false);
        }
    };

    const cancelDelete = () => {
        setIsConfirmDialogOpen(false);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return "file";
        
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return "file-pdf";
            case 'doc':
            case 'docx':
                return "file-word";
            default:
                return "file";
        }
    };

    const getFileTypeText = (fileName) => {
        if (!fileName) return "Unknown";
        
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return "PDF Document";
            case 'doc':
                return "Word Document (DOC)";
            case 'docx':
                return "Word Document (DOCX)";
            default:
                return extension?.toUpperCase() || "File";
        }
    };

    const getFileSize = (fileName) => {
        return "Size not available";
    };

    if (loading) {
        return (
            <main className="container">
                <div className="has-text-centered">
                    <p>Loading document details...</p>
                    <progress className="progress is-small is-primary" max="100">15%</progress>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <main className="container">
                <div className="notification is-danger">
                    {error}
                    <div className="mt-3">
                        <Link to="/dashboard/documents" className="button is-light">
                            Back to Documents
                        </Link>
                    </div>
                </div>
            </main>
        );
    }

    if (!documentItem) {
        return (
            <main className="container">
                <div className="notification is-warning">
                    Document not found.
                    <div className="mt-3">
                        <Link to="/dashboard/documents" className="button is-light">
                            Back to Documents
                        </Link>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="container">
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">Document Details</p>
                    </div>
                </div>
                <div className="level-right">
                    <div className="level-item">
                        <Link to="/dashboard/documents" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back to Documents
                        </Link>
                        &nbsp;&nbsp;
                        <button 
                            className="button is-info"
                            onClick={handleDownload}
                        >
                            <i className="fas fa-download"></i> &nbsp; Download
                        </button>
                        {canEdit(userRole) && (
                            <>
                                &nbsp;&nbsp;
                                <Link 
                                    to={`/dashboard/documents/${id}/update`} 
                                    className="button is-info"
                                >
                                    <i className="fas fa-edit"></i> &nbsp; Edit
                                </Link>
                                &nbsp;&nbsp;
                                <button 
                                    className="button is-danger"
                                    onClick={handleDeleteClick}
                                >
                                    <i className="fas fa-trash-alt"></i> &nbsp; Delete
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </nav>

            {notification.message && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    onClose={closeNotification}
                />
            )}

            <div className="columns">
                <div className="column is-two-thirds">
                    <div className="box">
                        <h2 className="title is-4">{documentItem.name}</h2>
                        
                        <div className="field is-horizontal">
                            <div className="field-label is-normal">
                                <label className="label">Description</label>
                            </div>
                            <div className="field-body">
                                <div className="field">
                                    <div className="control">
                                        <p className="text-content">
                                            {documentItem.description || "No description provided."}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="field is-horizontal">
                            <div className="field-label is-normal">
                                <label className="label">Document Type</label>
                            </div>
                            <div className="field-body">
                                <div className="field">
                                    <div className="control">
                                        <span className="tag is-info is-medium">
                                            {documentItem.document_type?.name || "N/A"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="field is-horizontal">
                            <div className="field-label is-normal">
                                <label className="label">Status</label>
                            </div>
                            <div className="field-body">
                                <div className="field">
                                    <div className="control">
                                        <span className={`tag is-medium ${documentItem.status === 'Active' ? 'is-success' : 'is-danger'}`}>
                                            {documentItem.status}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="field is-horizontal">
                            <div className="field-label is-normal">
                                <label className="label">File Information</label>
                            </div>
                            <div className="field-body">
                                <div className="field">
                                    <div className="control">
                                        <div className="notification is-light">
                                            <div className="level">
                                                <div className="level-left">
                                                    <div className="level-item">
                                                        <span className="icon is-large">
                                                            <i className={`fas fa-${getFileIcon(documentItem.file_name)} fa-2x`}></i>
                                                        </span>
                                                    </div>
                                                    <div className="level-item">
                                                        <div>
                                                            <p className="has-text-weight-bold">{documentItem.file_name}</p>
                                                            <p className="has-text-grey">{getFileTypeText(documentItem.file_name)}</p>
                                                            <p className="has-text-grey is-size-7">{getFileSize(documentItem.file_name)}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="level-right">
                                                    <div className="level-item">
                                                        <button 
                                                            className="button is-primary is-outlined"
                                                            onClick={handleDownload}
                                                        >
                                                            <span className="icon">
                                                                <i className="fas fa-download"></i>
                                                            </span>
                                                            <span>Download</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="column is-one-third">
                    <div className="box">
                        <h3 className="title is-5">Document Metadata</h3>
                        
                        <div className="content">
                            <div className="field is-horizontal">
                                <div className="field-label is-small">
                                    <label className="label">Created</label>
                                </div>
                                <div className="field-body">
                                    <div className="field">
                                        <div className="control">
                                            <p className="is-size-7">
                                                {documentItem.created_at && formatToDayMonthYear(documentItem.created_at)}
                                                {documentItem.creator && (
                                                    <span> by {documentItem.creator.first_name} {documentItem.creator.last_name}</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="field is-horizontal">
                                <div className="field-label is-small">
                                    <label className="label">Last Updated</label>
                                </div>
                                <div className="field-body">
                                    <div className="field">
                                        <div className="control">
                                            <p className="is-size-7">
                                                {documentItem.updated_at && formatToDayMonthYear(documentItem.updated_at)}
                                                {documentItem.updater && (
                                                    <span> by {documentItem.updater.first_name} {documentItem.updater.last_name}</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="field is-horizontal">
                                <div className="field-label is-small">
                                    <label className="label">Document ID</label>
                                </div>
                                <div className="field-body">
                                    <div className="field">
                                        <div className="control">
                                            <p className="is-size-7">{documentItem.id}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="box">
                        <h3 className="title is-5">Quick Actions</h3>
                        <div className="buttons are-small is-centered">
                            <button 
                                className="button is-primary is-fullwidth"
                                onClick={handleDownload}
                            >
                                <span className="icon">
                                    <i className="fas fa-download"></i>
                                </span>
                                <span>Download</span>
                            </button>
                            {canEdit(userRole) && (
                                <>
                                    <Link 
                                        to={`/dashboard/documents/${id}/update`} 
                                        className="button is-info is-fullwidth"
                                    >
                                        <span className="icon">
                                            <i className="fas fa-edit"></i>
                                        </span>
                                        <span>Edit Document</span>
                                    </Link>
                                    <button 
                                        className="button is-danger is-fullwidth"
                                        onClick={handleDeleteClick}
                                    >
                                        <span className="icon">
                                            <i className="fas fa-trash-alt"></i>
                                        </span>
                                        <span>Delete Document</span>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                theme="is-danger"
                title="Delete Document"
                message={`Are you sure you want to delete the document "${documentItem.name}"? This action cannot be undone.`}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                isLoading={false}
            />
        </main>
    );
}

export default DocumentDetails;